import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Mail,
  ScrollText,
  CalendarCheck,
  MessageSquare,
  Users,
  ClipboardList,
  Target,
  FileText,
  GraduationCap,
  BarChart3,
  BookOpen,
  FolderOpen,
  Upload,
  Layers,
  Library,
  Inbox,
  CircleHelp,
  PanelLeftClose,
  PanelLeftOpen,
  LogOut,
  CircleUser,
  Dot,
  Network,
  School,
  Link2,
  HeartHandshake,
  Sheet,
  type LucideIcon,
} from "lucide-react";
import { useAuth } from "@/auth/OidcProvider";
import { usePromotionPendingCount } from "@/pages/promotion/PromotionQueuePage";
import { shouldPollPromotionPendingCount } from "@/pages/promotion/pendingCount";
import { useTenant } from "@/tenants";
import { setLocale } from "@/i18n";
import { getVisibleNavItems } from "./sidebarNav";
import { canManageSchool, isGuardianOnly, useOrgMe } from "../api/school";
import { ChangePasswordModal } from "./ChangePasswordModal";

const LOCALE_LABELS: Record<string, string> = { en: "EN", "zh-CN": "中", fr: "FR" };
// Which locales the switcher offers is a tenant choice (librademo is EN/FR,
// 2026-09-11); see TenantConfig.locales.
function localesFor(tenant: { locales?: string[] }): { code: string; label: string }[] {
  const codes = tenant.locales && tenant.locales.length > 0 ? tenant.locales : ["en", "zh-CN"];
  return codes.map((code) => ({ code, label: LOCALE_LABELS[code] ?? code.toUpperCase() }));
}
// Sidebar no longer hosts the recents list — that moved into the chat
// view itself (see pages/workbench/ConversationsPanel) so each chat
// surface owns its own history with preview + delete.

// Per-route icons so the sidebar can collapse to a usable icon rail
// (rather than a blank strip). Keyed by NavItem.to; falls back to Dot.
const NAV_ICONS: Record<string, LucideIcon> = {
  "/": MessageSquare,
  "/classes": Users,
  "/admissions": ClipboardList,
  "/itarget": Target,
  "/assignments": FileText,
  "/exams": GraduationCap,
  "/reports": BarChart3,
  "/teaching-plans": BookOpen,
  "/documents": FolderOpen,
  "/my-promotions": Upload,
  "/library": Library,
  "/subjects": Layers,
  "/admin/promotions": Inbox,
  "/admin/schools": School,
  "/kp": Network,
  "/curriculum": CalendarCheck,
  "/admin/invitations": Mail,
  "/admin/access-logs": ScrollText,
  "/admin/dingtalk": Link2,
  "/gpa/overview": Sheet,
  "/parent": HeartHandshake,
};

export function Sidebar() {
  const tenantForLocales = useTenant();
  const LOCALES = localesFor(tenantForLocales);
  const { t, i18n } = useTranslation();
  const { state, signOut } = useAuth();
  const [pwOpen, setPwOpen] = useState(false);
  const tenant = useTenant();
  const location = useLocation();
  const isAuthed = state.status === "authenticated";
  const isAdmin = isAuthed && state.user.isAdmin;
  const role = isAuthed ? state.user.role : "";

  const pollPendingPromotions = shouldPollPromotionPendingCount({
    isAdmin,
    pathname: location.pathname,
  });
  const { data: pendingPromotions = 0 } = usePromotionPendingCount({
    enabled: pollPendingPromotions,
  });

  const hideForRole = role ? tenant.hideNavForRoles?.[role as keyof NonNullable<typeof tenant.hideNavForRoles>] ?? [] : [];
  const orgMe = useOrgMe();
  // 园长同样看得到管理分组(后端 is_school_manager 早已放行)
  const isPrincipal = canManageSchool(orgMe.data);
  const links = getVisibleNavItems({
    isAdmin, role, hideForRole, isPrincipal, modules: orgMe.data?.modules,
    isGuardian: orgMe.data?.is_guardian === true,
    guardianOnly: isGuardianOnly(orgMe.data),
  });
  // 钉钉首次登录自动建的账号:邮箱是占位地址、没有密码。
  const noPassword = orgMe.data?.has_password === false;
  const accountLine = noPassword ? String(t("account.dingtalkAccount")) : state.status === "authenticated" ? state.user.email : "";

  // Mobile (<md): nav collapses behind a hamburger. Auto-close on route change.
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Desktop (md+): collapse to an icon rail. Persisted across nav + reload.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem("nova_sidebar_collapsed") === "1";
    } catch {
      return false;
    }
  });
  const toggleCollapsed = () =>
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem("nova_sidebar_collapsed", next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });

  return (
    <aside className={`flex shrink-0 flex-col border-b border-[var(--school-border)] bg-white transition-[width] duration-200 md:h-screen md:border-b-0 md:border-r ${collapsed ? "md:w-16" : "md:w-52"}`}>
      <div className={`flex items-center gap-3 px-4 py-3 md:py-6 ${collapsed ? "md:justify-center md:px-0" : "md:px-5"}`}>
        <img src={tenant.brand.logoSrc} alt={tenant.displayName} className="h-10 w-10 shrink-0 rounded-lg" />
        <div className={`min-w-0 flex-1 ${collapsed ? "md:hidden" : ""}`}>
          <h1 className="truncate whitespace-nowrap text-[15px] font-semibold tracking-normal text-[var(--school-text)]">
            {tenant.displayName}
          </h1>
          <p className="truncate whitespace-nowrap text-[11px] font-medium tracking-normal text-[var(--school-muted)]">
            {tenant.tagline}
          </p>
        </div>
        <button
          onClick={() => setMenuOpen((o) => !o)}
          aria-expanded={menuOpen}
          aria-label={t("nav.menu")}
          className="school-focus-ring rounded-lg p-2 text-[var(--school-muted)] hover:bg-[var(--school-surface-muted)] hover:text-[var(--school-text)] md:hidden"
        >
          <svg viewBox="0 0 20 20" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            {menuOpen ? <path d="M5 5 L15 15 M15 5 L5 15" /> : <path d="M3.5 5.5 H16.5 M3.5 10 H16.5 M3.5 14.5 H16.5" />}
          </svg>
        </button>
      </div>
      <div className={`h-px bg-[var(--school-border)] ${collapsed ? "md:mx-3" : "mx-4 md:mx-5"}`} />
      <nav className={`${menuOpen ? "flex" : "hidden"} flex-col gap-1 px-3 py-3 md:mt-4 md:flex md:gap-0.5 md:overflow-y-auto md:py-0`}>
        {links.map((l, i) => {
          const showBadge = l.to === "/admin/promotions" && pendingPromotions > 0;
          const prevGroup = i > 0 ? (links[i - 1]?.group ?? "main") : null;
          const thisGroup = l.group ?? "main";
          const startsAdminGroup = thisGroup === "admin" && prevGroup !== "admin";
          const Icon = NAV_ICONS[l.to] ?? Dot;
          return (
            <div key={l.to} className="contents">
              {startsAdminGroup && (
                <div className={`mt-3 border-t border-[var(--school-border)] pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--school-muted)] ${collapsed ? "md:hidden" : "px-3.5"}`}>
                  {t("nav.adminSection")}
                </div>
              )}
              <NavLink
                to={l.to}
                end={l.to === "/"}
                title={collapsed ? String(t(l.labelKey)) : undefined}
                className={({ isActive }) =>
                  `school-focus-ring relative flex shrink-0 items-center gap-2.5 rounded-lg py-2.5 text-[13px] font-medium transition-colors ${
                    collapsed ? "md:justify-center md:px-0" : "px-3.5"
                  } ${
                    isActive
                      ? "bg-[var(--school-accent-soft)] text-[var(--school-accent)]"
                      : "text-[var(--school-muted)] hover:bg-[var(--school-surface-muted)] hover:text-[var(--school-text)]"
                  }`
                }
              >
                <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.8} aria-hidden />
                <span className={`flex-1 truncate ${collapsed ? "md:hidden" : ""}`}>{t(l.labelKey)}</span>
                {showBadge && (
                  <span className={collapsed ? "absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-[var(--school-accent)] md:right-1 md:top-1" : "rounded-full bg-[var(--school-accent)] px-2 py-0.5 text-[10px] font-semibold text-white"}>
                    {collapsed ? "" : pendingPromotions}
                  </span>
                )}
              </NavLink>
            </div>
          );
        })}
      </nav>

      <div className="mt-auto flex flex-col gap-1 px-3 py-3">
        {/* 老师使用手册 — 静态 HTML 挂在 /guide/,新标签页打开不打断当前工作。 */}
        <a
          href="/guide/"
          target="_blank"
          rel="noreferrer"
          title={collapsed ? String(t("nav.userGuide")) : undefined}
          className={`school-focus-ring items-center gap-2.5 rounded-lg py-2.5 text-[13px] font-medium text-[var(--school-muted)] hover:bg-[var(--school-surface-muted)] hover:text-[var(--school-text)] ${menuOpen ? "flex" : "hidden md:flex"} ${collapsed ? "md:justify-center md:px-0" : "px-3.5"}`}
        >
          <CircleHelp className="h-[18px] w-[18px] shrink-0" strokeWidth={1.8} aria-hidden />
          <span className={collapsed ? "md:hidden" : ""}>{t("nav.userGuide")}</span>
        </a>
        {/* Language switch — moved here out of the old full-width top
            header (which held nothing else). Full EN/中 segment when the
            rail is expanded (or the mobile menu is open); a single
            cycling button when collapsed to the icon rail. */}
        {/* 当前登录账户(名字 + 邮箱)。折叠时只留图标,悬停看详情。 */}
        {isAuthed && (
          <div
            title={`${state.user.name || state.user.email} · ${accountLine}`}
            className={`${menuOpen ? "flex" : "hidden md:flex"} items-center gap-2.5 rounded-lg py-2 ${collapsed ? "md:justify-center md:px-0" : "px-3.5"}`}
          >
            <CircleUser className="h-[18px] w-[18px] shrink-0 text-[var(--school-muted)]" strokeWidth={1.8} aria-hidden />
            <div className={`min-w-0 ${collapsed ? "md:hidden" : ""}`}>
              <p className="truncate text-[13px] font-medium leading-tight text-[var(--school-text)]">
                {state.user.name || state.user.email}
              </p>
              <p className="truncate text-[11px] leading-tight text-[var(--school-muted)]">{accountLine}</p>
              {!noPassword && (
                <button type="button" onClick={() => setPwOpen(true)} className="mt-0.5 text-[11px] text-sky-600 hover:underline" data-testid="change-password">
                  {t("account.changePassword")}
                </button>
              )}
            </div>
          </div>
        )}
        {pwOpen && <ChangePasswordModal onClose={() => setPwOpen(false)} />}
        <div
          className={`${menuOpen ? "flex" : "hidden md:flex"} ${collapsed ? "md:hidden" : ""} rounded-lg border border-[var(--school-border)] bg-[var(--school-surface-muted)] p-0.5 text-xs font-medium`}
        >
          {LOCALES.map((l) => (
            <button
              key={l.code}
              onClick={() => setLocale(l.code)}
              className={`school-focus-ring flex-1 rounded-md px-2.5 py-1.5 transition-colors ${
                i18n.language === l.code
                  ? "bg-white text-[var(--school-text)] shadow-sm"
                  : "text-[var(--school-muted)] hover:text-[var(--school-text)]"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => {
            const codes = LOCALES.map((l) => l.code);
            const next = codes[(Math.max(0, codes.indexOf(i18n.language)) + 1) % codes.length] ?? codes[0] ?? "en";
            setLocale(next);
          }}
          title={LOCALES.find((l) => l.code !== i18n.language)?.label ?? ""}
          className={`school-focus-ring hidden items-center justify-center rounded-lg py-2.5 text-[13px] font-semibold text-[var(--school-muted)] hover:bg-[var(--school-surface-muted)] hover:text-[var(--school-text)] ${collapsed ? "md:flex" : "md:hidden"}`}
        >
          {LOCALES.find((l) => l.code === i18n.language)?.label ?? i18n.language.toUpperCase()}
        </button>
        <button
          onClick={toggleCollapsed}
          aria-label={String(t(collapsed ? "nav.expand" : "nav.collapse"))}
          title={String(t(collapsed ? "nav.expand" : "nav.collapse"))}
          className={`school-focus-ring hidden items-center gap-2.5 rounded-lg py-2.5 text-[13px] font-medium text-[var(--school-muted)] hover:bg-[var(--school-surface-muted)] hover:text-[var(--school-text)] md:flex ${collapsed ? "md:justify-center md:px-0" : "px-3.5"}`}
        >
          {collapsed ? <PanelLeftOpen className="h-[18px] w-[18px] shrink-0" strokeWidth={1.8} aria-hidden /> : <PanelLeftClose className="h-[18px] w-[18px] shrink-0" strokeWidth={1.8} aria-hidden />}
          <span className={collapsed ? "md:hidden" : ""}>{t(collapsed ? "nav.expand" : "nav.collapse")}</span>
        </button>
        {isAuthed && (
          <button
            onClick={signOut}
            className={`school-focus-ring items-center gap-2.5 rounded-lg py-2.5 text-left text-[13px] font-medium text-[var(--school-muted)] hover:bg-red-50 hover:text-[var(--school-danger)] ${menuOpen ? "flex" : "hidden md:flex"} ${collapsed ? "md:justify-center md:px-0" : "px-3.5"}`}
          >
            <LogOut className="h-[18px] w-[18px] shrink-0" strokeWidth={1.8} aria-hidden />
            <span className={collapsed ? "md:hidden" : ""}>{t("nav.signOut")}</span>
          </button>
        )}
        {tenant.brand.poweredBy && (
          <p className={`${menuOpen ? "block" : "hidden md:block"} ${collapsed ? "md:hidden" : ""} px-3.5 pt-1 text-[11px] tracking-wide text-[var(--school-muted)] opacity-70`} data-testid="powered-by">
            <span className="inline-flex items-center gap-1">Powered by <img src="/libraos-mark.svg" alt="" className="h-3.5 w-3.5" /><span className="font-semibold">{tenant.brand.poweredBy}</span></span>
          </p>
        )}
      </div>
    </aside>
  );
}
